/**
 * @deprecated Este componente foi separado em CustomerSatisfaction.tsx e ResolutionRate.tsx
 * para permitir melhor flexibilidade no dashboard.
 * Use os componentes individuais em vez deste.
 */
import React from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Progress } from '@/components/ui/progress';
import { Badge } from '@/components/ui/badge';
import { Star, Target, ThumbsUp } from 'lucide-react';
import { SatisfactionMetrics as SatisfactionData } from '@/hooks/useDashboardMetrics';

interface SatisfactionMetricsProps {
  data: SatisfactionData | undefined;
  isLoading: boolean;
}

const SatisfactionMetrics = ({ data, isLoading }: SatisfactionMetricsProps) => {
  if (isLoading) {
    return (
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-6">
        {[1, 2].map((i) => (
          <Card key={i}>
            <CardHeader>
              <div className="h-6 bg-gray-200 rounded animate-pulse"></div>
            </CardHeader>
            <CardContent>
              <div className="text-center animate-pulse">
                <div className="h-12 bg-gray-200 rounded mb-2"></div>
                <div className="h-4 bg-gray-200 rounded mb-2"></div>
                <div className="h-2 bg-gray-200 rounded"></div>
              </div>
            </CardContent>
          </Card>
        ))}
      </div>
    );
  }

  if (!data) return null;

  const getNPSColor = (score: number) => {
    if (score >= 8) return 'text-green-600';
    if (score >= 6) return 'text-yellow-600';
    return 'text-red-600';
  };

  const getNPSBadge = (score: number) => {
    if (score >= 8) return { text: 'Excelente', variant: 'default' as const, color: 'bg-green-100 text-green-800' };
    if (score >= 6) return { text: 'Bom', variant: 'secondary' as const, color: 'bg-yellow-100 text-yellow-800' };
    return { text: 'Precisa Melhorar', variant: 'destructive' as const, color: 'bg-red-100 text-red-800' };
  };

  const getResolutionColor = (percentage: number) => {
    if (percentage >= 90) return 'text-green-600';
    if (percentage >= 75) return 'text-yellow-600';
    return 'text-red-600';
  };

  const renderStars = (rating: number) => {
    return Array.from({ length: 5 }, (_, i) => (
      <Star 
        key={i} 
        className={`h-4 w-4 ${i < Math.floor(rating) ? 'text-yellow-400 fill-current' : 'text-gray-300'}`} 
      />
    ));
  };

  const npsStatus = getNPSBadge(data.npsScore);

  return (
    <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-6">
      {/* Satisfação do Cliente */}
      <Card className="hover:shadow-lg transition-all duration-300">
        <CardHeader>
          <CardTitle className="flex items-center gap-2 text-yellow-600">
            <Star className="h-5 w-5" />
            Satisfação do Cliente
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="text-center">
            {/* NPS Score */}
            <div className={`text-4xl font-bold mb-2 ${getNPSColor(data.npsScore)}`}>
              {data.npsScore}
            </div>
            <p className="text-sm text-gray-600 mb-3">NPS Score</p>
            
            {/* Badge de Status */}
            <Badge className={`mb-4 ${npsStatus.color}`}>
              {npsStatus.text}
            </Badge>

            {/* Avaliação por Estrelas */}
            <div className="flex justify-center mb-3">
              {renderStars(data.customerRating)}
            </div>
            <p className="text-xs text-gray-500 mb-4">
              {data.customerRating}/5.0 - Avaliação média
            </p>

            {/* Progress Bar */}
            <div className="space-y-2">
              <div className="flex justify-between text-xs">
                <span>Meta: 8.0</span>
                <span className={data.npsScore >= 8 ? 'text-green-600' : 'text-red-600'}>
                  {data.npsScore >= 8 ? '✓ Meta atingida' : '⚠ Abaixo da meta'}
                </span>
              </div>
              <Progress value={(data.npsScore / 10) * 100} className="h-2" />
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Taxa de Resolução */}
      <Card className="hover:shadow-lg transition-all duration-300">
        <CardHeader>
          <CardTitle className="flex items-center gap-2 text-blue-600">
            <Target className="h-5 w-5" />
            Taxa de Resolução
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="text-center">
            {/* Percentage */}
            <div className={`text-4xl font-bold mb-2 ${getResolutionColor(data.firstContactResolution)}`}>
              {data.firstContactResolution}%
            </div>
            <p className="text-sm text-gray-600 mb-4">Primeiro Contato</p>

            {/* Progress Circle Visual */}
            <div className="relative w-24 h-24 mx-auto mb-4">
              <svg className="w-24 h-24 transform -rotate-90" viewBox="0 0 100 100">
                <circle
                  cx="50"
                  cy="50"
                  r="40"
                  stroke="#e5e7eb"
                  strokeWidth="8"
                  fill="none"
                />
                <circle
                  cx="50"
                  cy="50"
                  r="40"
                  stroke={data.firstContactResolution >= 90 ? '#22c55e' : data.firstContactResolution >= 75 ? '#eab308' : '#ef4444'}
                  strokeWidth="8"
                  fill="none"
                  strokeDasharray={`${2 * Math.PI * 40}`}
                  strokeDashoffset={`${2 * Math.PI * 40 * (1 - data.firstContactResolution / 100)}`}
                  className="transition-all duration-500"
                />
              </svg>
              <div className="absolute inset-0 flex items-center justify-center">
                <ThumbsUp className={`h-6 w-6 ${getResolutionColor(data.firstContactResolution)}`} />
              </div>
            </div>

            {/* Status e Meta */}
            <div className="space-y-2">
              <div className="flex justify-between text-xs">
                <span>Meta: 90%</span>
                <span className={data.firstContactResolution >= 90 ? 'text-green-600' : 'text-red-600'}>
                  {data.firstContactResolution >= 90 ? '✓ Meta atingida' : '⚠ Abaixo da meta'}
                </span>
              </div>
              <Progress value={data.firstContactResolution} className="h-2" />
            </div>

            {/* Informações Adicionais */}
            <div className="mt-4 p-3 bg-gray-50 rounded-lg">
              <div className="text-xs text-gray-600">
                <div className="flex justify-between mb-1">
                  <span>Resolvidos no 1º contato:</span>
                  <span className="font-semibold">
                    {Math.round((data.firstContactResolution / 100) * 150)} de 150
                  </span>
                </div>
                <div className="flex justify-between">
                  <span>Necessitaram follow-up:</span>
                  <span className="font-semibold">
                    {150 - Math.round((data.firstContactResolution / 100) * 150)} tickets
                  </span>
                </div>
              </div>
            </div>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

export default SatisfactionMetrics;
